<template>
    <div>
        <ValidationObserver v-slot="{ dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('commons.object')"
                        v-model="invoice.object"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        @blur="save()"
                        ref="objectInput"
                    />
                    <app-input-text
                        :label="$t('commons.code')"
                        v-model="invoice.code"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="invoice.emitter"
                        :options="bundles"
                        @input="onBundleChange"
                    ></app-bundle-picker>
                    <app-number-input
                        :label="$t('commons.amountTTC')"
                        v-model="invoice.amountTTC"
                        :allowsNegative="true"
                        @blur="onAmountChange"
                        :currency="currencyFormat.currency"
                    />
                </app-fieldset>

                <app-fieldset class="col-span-2 mt-5" :label="$t('commons.dates')">
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="invoice.emissionDate"
                        :label="$t('invoices.emissionDate')"
                        @input="save()"
                    />
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="invoice.invoiceDate"
                        :label="$t('invoices.invoiceDate')"
                        @input="save()"
                    />
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-4" :label="$t('invoices.relatedQuotes')">
                    <table class="w-full col-span-2 text-xs">
                        <tr class="border-b">
                            <th class="p-2 border text-left">{{ $t('amendments.quote') }}</th>
                            <th class="p-2 border text-left">{{ $t('commons.type') }}</th>
                            <th class="p-2 border text-right">{{ $t('invoices.quoteAmountTTC') }}</th>
                            <th class="p-2 border text-right">{{ $t('invoices.priorRemainingAmountTTC') }}</th>
                            <th class="p-2 border text-right bg-blue-200">
                                <span :title="$t('invoices.subAmountTTCTips')">{{ $t('invoices.subAmountTTC') }}</span>
                            </th>
                            <th class="p-2 border text-right">{{ $t('invoices.remainingAmountTTC') }}</th>
                        </tr>
                        <tr class="border-b" v-for="amendment in chargedAmendments">
                            <td class="border p-2">
                                <router-link
                                    class="hover:underline"
                                    :to="{
                                        name: 'followAmendment',
                                        params: {
                                            ...$route.params,
                                            amendmentId: amendment.id,
                                        },
                                    }"
                                >
                                    <span class="text-xs">{{ amendment.code }}&nbsp;</span>
                                    <span>{{ amendment.object }}</span>
                                </router-link>
                            </td>
                            <td class="border p-2">
                                {{ amendment.type ? $t('invoices.amendmentSimplifiedTypes.' + amendment.type) : '' }}
                            </td>
                            <td class="border p-2 text-right">
                                <span>
                                    {{ amendment.amountTTC ? $n(amendment.amountTTC, currencyFormat) : '-' }}
                                </span>
                            </td>
                            <td class="border p-2">
                                <div class="flex justify-end gap-1 items-center">
                                    <span>
                                        {{
                                            amendment.remainingAmountTTC
                                                ? $n(amendment.remainingAmountTTC, currencyFormat)
                                                : '-'
                                        }}
                                    </span>
                                </div>
                            </td>
                            <td class="border p-2 text-right bg-blue-100">
                                <div
                                    v-if="
                                        amendment.quoteStatus !== 'validated' ||
                                        amendment.amendmentStatus !== 'signed' ||
                                        amendment.SOStatus !== 'signed'
                                    "
                                    :title="
                                        amendment.quoteStatus !== 'validated'
                                            ? $t('invoices.invoiceReadyNotValidatedQuote')
                                            : amendment.amendmentStatus !== 'signed'
                                            ? $t('invoices.invoiceReadyNotSignedAmendment')
                                            : amendment.SOStatus !== 'signed'
                                            ? $t('invoices.invoiceReadyNotSignedSO')
                                            : ''
                                    "
                                    class="text-red-500 w-full flex justify-center"
                                >
                                    <icon-alert-outline width="16px" height="16px" />
                                </div>
                                <div v-else class="flex justify-between items-center">
                                    <app-button
                                        v-if="amendment.remainingAmountTTC > 0"
                                        size="mini"
                                        variant="borderless"
                                        icon="icon-arrow-right-bold-box"
                                        class="opacity-10 hover:opacity-100"
                                        :title="$t('invoices.copyRemaining')"
                                        @click="copyRemainingAmountToChargedAmount(amendment)"
                                    ></app-button>
                                    <app-number-link
                                        :show-label="false"
                                        :label="$t('invoices.subAmountTTC')"
                                        v-model="amendment.chargedAmountTTC"
                                        :positiveOnly="false"
                                        format="decimal"
                                        @input="onAmendmentAmountChanged(amendment)"
                                        :currency="currencyFormat.currency"
                                        :disabled="
                                            amendment.quoteStatus !== 'validated' ||
                                            amendment.amendmentStatus !== 'signed' ||
                                            amendment.SOStatus !== 'signed'
                                        "
                                    >
                                        <div class="flex gap-1">
                                            <u class="w-full text-right">
                                                {{ $n(amendment.chargedAmountTTC || 0, currencyFormat) }}
                                            </u>
                                            <span
                                                :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                                v-if="amendment.amountTTC && amendment.amountTTC > 0"
                                                :class="{
                                                    'text-red-500 font-bold':
                                                        amendment.chargedAmountTTC > amendment.amountTTC,
                                                }"
                                            >
                                                ({{
                                                    Math.round(
                                                        (amendment.chargedAmountTTC / amendment.amountTTC) * 100,
                                                    )
                                                }}%)
                                            </span>
                                        </div>
                                    </app-number-link>
                                </div>
                            </td>
                            <td class="border p-2 text-right">
                                <span>
                                    {{
                                        amendment.remainingAmountTTC && amendment.chargedAmountTTC
                                            ? $n(
                                                  amendment.remainingAmountTTC - amendment.chargedAmountTTC,
                                                  currencyFormat,
                                              )
                                            : '-'
                                    }}
                                </span>
                                <span
                                    :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                    v-if="
                                        amendment.amountTTC &&
                                        amendment.remainingAmountTTC &&
                                        amendment.chargedAmountTTC
                                    "
                                    :class="{
                                        'text-red-500 font-bold':
                                            amendment.remainingAmountTTC < amendment.chargedAmountTTC,
                                    }"
                                >
                                    ({{
                                        Math.round((amendment.chargedAmountTTC / amendment.remainingAmountTTC) * 100)
                                    }}%)
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2"></td>
                            <td class="border p-2 text-right">{{ $n(totalQuotes, currencyFormat) }}</td>
                            <td class="border p-2 text-right">
                                {{ $n(totalPriorRemainingAmountTTC, currencyFormat) }}
                            </td>
                            <td class="border p-2">
                                <div
                                    class="flex justify-center gap-1"
                                    :class="{
                                        'text-red-500 font-bold': totalChargedAmendments !== invoice.amountTTC,
                                    }"
                                    :title="
                                        totalChargedAmendments !== invoice.amountTTC
                                            ? $t('invoices.distributedAmountDoesNotMatchInvoiceAmount')
                                            : ''
                                    "
                                >
                                    <span>
                                        {{ $n(totalChargedAmendments, currencyFormat) }}
                                    </span>
                                </div>
                            </td>
                            <td class="border p-2 text-right">{{ $n(totalRemainingAmountTTC, currencyFormat) }}</td>
                        </tr>
                    </table>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.distribution')" v-if="invoice.emitter">
                    <table class="w-full border text-xs col-span-2">
                        <tr class="border-b">
                            <th class="p-2 text-left">{{ $t('invoices.company') }}</th>
                            <th class="p-2 text-right bg-blue-200">{{ $t('commons.amountTTC') }}</th>
                        </tr>
                        <tr class="border-b">
                            <td class="border p-2" style="width: 70%">
                                <span>
                                    <b>[{{ $t('invoices.mainCompany') }}]</b>
                                    {{ invoice.emitter?.company?.name }}
                                </span>
                            </td>
                            <td class="border p-2 text-right bg-blue-200">
                                {{
                                    $n(
                                        invoice.distribution?.find((line) => line.id === invoice.emitterId)
                                            ?.amountTTC || 0,
                                        currencyFormat,
                                    )
                                }}
                            </td>
                        </tr>
                        <tr class="border-b" v-for="subcontractor in subcontractors">
                            <td class="border p-2">
                                <span>{{ subcontractor.name }}</span>
                            </td>
                            <td class="border p-2 text-right bg-blue-200">
                                <app-number-link
                                    :label="$t('commons.amountTTC')"
                                    :show-label="false"
                                    v-model="subcontractor.amountTTC"
                                    @input="saveDistribution()"
                                    :positiveOnly="false"
                                    format="decimal"
                                    class="underline"
                                >
                                    <span class="w-full text-right">
                                        {{ $n(subcontractor.amountTTC, currencyFormat) }}
                                    </span>
                                </app-number-link>
                            </td>
                        </tr>
                    </table>
                </app-fieldset>
                <div class="col-span-2 mt-5">
                    <app-separator
                        :label="$t('invoices.attachment')"
                        icon="icon-file-document-outline"
                        class="my-5 col-span-4"
                    ></app-separator>
                    <div class="flex">
                        <app-upload-button
                            ref="fileInput"
                            icon="icon-paperclip"
                            :label="invoice.url ? $t('invoices.replaceDocument') : $t('invoices.newDocument')"
                            :end-point="`/api/projects/${$route.params.projectId}/invoices/${$route.params.invoiceId}/attachments`"
                            :name="$t('invoices.newDocument')"
                            class="text-sm offline:hidden"
                            @loaded="onLoaded"
                        />
                    </div>
                    <table class="w-full my-5" v-if="invoice.url">
                        <tr class="border-b hover:bg-gray-200">
                            <td class="p-2" style="width: 30px">
                                <a
                                    :href="invoice.url"
                                    :download="invoice.fileName"
                                    aria-label="download attachment"
                                    target="_blank"
                                >
                                    <IconDownload width="16" heigth="16" />
                                </a>
                            </td>
                            <td class="p-2 text-left">
                                <a :href="invoice.url" class="text-sm" aria-label="view attachment" target="_blank">
                                    {{ invoice.fileName }}
                                </a>
                            </td>
                            <td class="p-2" style="width: 30px" v-if="isAdmin">
                                <app-button
                                    size="mini"
                                    variant="danger"
                                    aria-label="remove attachment"
                                    icon="icon-trash-can-outline"
                                    @click="removeAttachment()"
                                />
                            </td>
                        </tr>
                    </table>
                </div>
                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
import { getMapById, reduceSumOn, sanitize, sortBy } from '@/services/sanitize.service';
import { getInvoice, getInvoices, updateInvoice } from './invoices.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import { queryProject } from '@/features/projects/projects.service';
import { confirm } from '@/features/dialogs/dialogs.service';
import { getInvoiceReviews } from '@/features/invoices/invoiceReviews.service';
import { getAmendments } from '@/features/amendments/amendments.service';
import { getCIEs } from '@/features/cie/CIE.service';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppInputText from '@/components/appInputText/AppInputText.vue';
import AppBundlePicker from '@/components/appBundlePicker.vue';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import AppUploadButton from '@/components/app-uploadButton/AppUploadButton.vue';
import IconAlertOutline from '@/icons/IconAlertOutline.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import AppSaveOnLeave from '@/components/AppSaveOnLeave.vue';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import IconDownload from '@/icons/IconDownload.vue';
import { buildSubcontractors, getNewSituationDistribution } from '@/features/statements/statements.service';

export default {
    components: {
        IconDownload,
        AppMultiPicker,
        AppSaveOnLeave,
        AppNumberLink,
        AppButton,
        IconAlertOutline,
        AppUploadButton,
        AppSeparator,
        AppDateInput,
        AppNumberInput,
        AppBundlePicker,
        AppInputText,
        AppFieldset,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
            this.isAdmin = project.me.allowedFeatures.includes('project_invoices');
            this.project = project;
        });
        this.init();
    },
    watch: {
        $route: function (from, to) {
            if (from.name !== to.name || from.params.invoiceId !== to.params.invoiceId) {
                this.focusDone = false;
            }
            this.init();
        },
    },
    computed: {
        totalChargedAmendments() {
            return reduceSumOn(this.chargedAmendments, 'chargedAmountTTC');
        },
        totalQuotes() {
            return reduceSumOn(this.chargedAmendments, 'amountTTC');
        },
        totalPriorRemainingAmountTTC() {
            return reduceSumOn(this.chargedAmendments, 'remainingAmountTTC');
        },
        totalRemainingAmountTTC() {
            return reduceSumOn(this.chargedAmendments, (item) =>
                item.remainingAmountTTC && item.chargedAmountTTC ? item.remainingAmountTTC - item.chargedAmountTTC : 0,
            );
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getInvoice(this.$route.params.projectId, this.$route.params.invoiceId),
                    getInvoices(this.$route.params.projectId),
                    getInvoiceReviews(this.$route.params.projectId, this.$route.params.invoiceId),
                    getAmendments(this.$route.params.projectId),

                    getCIEs(this.$route.params.projectId),
                ]).subscribe(async ([bundles, companies, invoice, allInvoices, amendments, cie]) => {
                    const relevantCie = cie.filter((cie) => cie.quoteStatus && cie.quoteStatus === 'validated');
                    const companyMap = getMapById(companies);
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                    }));
                    this.bundleMap = getMapById(mappedBundles);

                    const amountTTCAlreadyChargedByAmendmentId = allInvoices
                        .filter(
                            (anInvoice) =>
                                anInvoice.conclusion === 'validated' &&
                                anInvoice.id !== invoice.id &&
                                anInvoice.situationEmissionDate &&
                                anInvoice.situationEmissionDate.getTime() <
                                    (invoice.invoiceDate || invoice.emissionDate || invoice.createdAt).getTime(),
                        )
                        .reduce((acc, invoice) => {
                            if (invoice.distributionByAmendments) {
                                return [...acc, ...invoice.distributionByAmendments];
                            } else {
                                return acc;
                            }
                        }, [])
                        .reduce((acc, distribution) => {
                            if (acc[distribution.id]) {
                                acc[distribution.id] += distribution.amountTTC;
                            } else {
                                acc[distribution.id] = distribution.amountTTC;
                            }
                            return acc;
                        }, {});
                    this.invoice = {
                        distributionByAmendments: [],
                        ...invoice,
                        emitter: this.bundleMap[invoice.emitterId],
                    };
                    this.bundles = mappedBundles;
                    this.selectedVersion = this.$route.params.invoiceId;
                    this.subcontractors = await buildSubcontractors(
                        this.$route.params.projectId,
                        this.invoice.emitterId,
                        this.invoice.distribution,
                    );
                    this.amendments = sortBy(
                        [
                            ...amendments.filter((amendment) => amendment.quoteStatus === 'validated'),
                            ...relevantCie
                                .filter((cie) => cie.bundleId === invoice.emitterId)
                                .map((cie) => ({ ...cie, type: 'cie' })),
                        ],
                        (amendment) => (amendment.code || 'zzz') + amendment.object,
                    );
                    this.chargedAmendments = this.amendments
                        .filter((amendment) => amendment.bundleId === invoice.emitterId)
                        .map((amendment) => ({
                            ...amendment,
                            remainingAmountTTC:
                                amendment.amountTTC - (amountTTCAlreadyChargedByAmendmentId[amendment.id] || 0),
                            chargedAmountTTC:
                                this.invoice.distributionByAmendments.find(
                                    (chargedAmendment) => chargedAmendment.id === amendment.id,
                                )?.amountTTC || 0,
                        }));
                }),
            ];
        },
        computeNewDistribution() {
            const cleanDistribution = this.subcontractors.filter((line) => line && line.amountTTC !== 0);
            const totalDistributionTTC = reduceSumOn(cleanDistribution, 'amountTTC');
            return [
                {
                    id: this.invoice.emitterId,
                    name: this.invoice.emitter?.company?.name,
                    amountTTC: this.invoice.amountTTC - totalDistributionTTC,
                },
                ...cleanDistribution,
            ];
        },
        async saveDistribution() {
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        copyRemainingAmountToChargedAmount(amendment) {
            amendment.chargedAmountTTC = amendment.remainingAmountTTC;
            this.onAmendmentAmountChanged();
        },
        onAmendmentAmountChanged() {
            this.saveDistribution();
            updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                distributionByAmendments: this.chargedAmendments
                    .map((chargedAmendment) => ({
                        id: chargedAmendment.id,
                        amountTTC: chargedAmendment.chargedAmountTTC,
                    }))
                    .filter((chargedAmendment) => chargedAmendment.amountTTC !== 0),
            });
        },
        async onBundleChange() {
            this.invoice.distribution = [];
            this.subcontractors = await buildSubcontractors(
                this.$route.params.projectId,
                this.invoice.emitterId,
                this.invoice.distribution,
            );
            return updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                emitterId: this.invoice.emitter.id,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        onAmountChange() {
            return updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                amountTTC: this.invoice.amountTTC,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        computeNewSituationDistribution() {
            let cleanDistribution = this.invoice.situationDistribution.filter(
                (line) => line.id !== this.invoice.emitterId && line.amountTTC && line.amountTTC !== 0,
            );
            if (cleanDistribution.length === 0) {
                cleanDistribution = this.subcontractors.filter(
                    (line) => line.id !== this.invoice.emitterId && line.amountTTC && line.amountTTC !== 0,
                );
            }
            return getNewSituationDistribution(this.invoice, cleanDistribution);
        },
        sanitizeInvoice(invoice) {
            return {
                ...sanitize(invoice, ['emitter', 'versions']),
                emitterId: invoice.emitter ? invoice.emitter.id : null,
            };
        },
        async save() {
            const invoiceEntity = this.sanitizeInvoice(this.invoice);
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                ...invoiceEntity,
            });
            if (this.$refs.observer) {
                this.$refs.observer.reset();
            }
        },
        onLoaded(attachment) {
            this.invoice.url = attachment.url;
            this.invoice.fileName = attachment.fileName;
        },
        async removeAttachment() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                this.invoice.url = null;
                this.invoice.fileName = null;
                await this.save();
            }
        },
    },
    data() {
        return {
            amendments: [],
            chargedAmendments: [],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            subcontractors: [],
            subscriptions: [],
            bundleMap: {},
            bundles: [],
            invoice: {
                name: '',
                emitter: null,
                versions: [],
                distributionByAmendments: [],
            },
            selectedVersion: null,
            isAdmin: false,
            project: {},
        };
    },
};
</script>
